import { useApiClient } from '~/services/api'
import { AppProblemModel } from '~/services/api/AppProblemModel'
import { useLogger } from '~/composables/useLogger'
import { isEngineeringEnvironment } from '~/utils/isEngineeringEnvironment'
import { pendingTwoFactorChallenge } from '~/utils/pendingTwoFactorChallenge'

const NOT_FOUND_STATUS = 404

// Fills only on engineering environments; a 404 means the route is absent, other failures are logged.
export function useTwoFactorTestAssist() {
  const { getChallengeCode, getStepUpCode } = useApiClient().testAssist()
  const logger = useLogger('twoFactorTestAssist')
  const enabled = isEngineeringEnvironment()

  const reportUnlessAbsent = (route: string, error: unknown): void => {
    const problem = AppProblemModel.normalize(error)
    if (problem.status !== NOT_FOUND_STATUS) {
      logger.warn('testAssistUnavailable', { route, status: problem.status })
    }
  }

  async function fillChallengeCode(apply: (code: string) => void): Promise<void> {
    const pending = pendingTwoFactorChallenge.peek()
    if (!enabled || pending === undefined) return
    try {
      apply((await getChallengeCode(pending.challengeToken)).code)
    } catch (error: unknown) {
      reportUnlessAbsent('challenge', error)
    }
  }

  async function fillStepUpCode(apply: (code: string) => void): Promise<void> {
    if (!enabled) return
    try {
      apply((await getStepUpCode()).code)
    } catch (error: unknown) {
      reportUnlessAbsent('step-up', error)
    }
  }

  return { fillChallengeCode, fillStepUpCode }
}
